/* eslint-disable import/no-named-as-default */
import React, { useLayoutEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import { StaticQuery, graphql } from 'gatsby';

import Header from '../Header';
import Footer from '../Footer';
import theme, { GlobalStyles } from '../../../themes';
import { IntlContext } from '../../Providers/Intl';

import { Main } from './styles';

const Layout = ({
  data: {
    prodigy: { localeMappings },
  },
  children,
  cmsTheme,
  lang,
}) => {
  const updatedTheme = cmsTheme
    ? {
        ...theme,
        colors: {
          ...theme.colors,
          ...cmsTheme,
        },
      }
    : theme;

  // store the latest cmsTheme ref we've seen for later comparison
  const cmsThemeCache = useRef(cmsTheme);
  // theme being used by the provider
  const buildTheme = useRef(updatedTheme);

  // if a cmsTheme is provided and it doesn't match the one currently cached
  if (cmsTheme && cmsThemeCache.current !== cmsTheme) {
    cmsThemeCache.current = cmsTheme;
    buildTheme.current = updatedTheme;
  }

  const { setIntl } = useContext(IntlContext);
  useLayoutEffect(() => {
    setIntl({
      lang,
      path: localeMappings.find(({ locale }) => locale === lang).path,
    });
  }, []);

  return (
    <ThemeProvider theme={buildTheme.current}>
      <>
        <GlobalStyles />
        <Header />
        <Main id="content">{children}</Main>
        <Footer />
      </>
    </ThemeProvider>
  );
};

// * StaticQuery Component that retrieves and passes in all the articles
const withStaticQueryRedirect = ({ children, cmsTheme, lang }) => {
  return (
    <StaticQuery
      query={graphql`
        query LayoutLocaleMappingQuery {
          prodigy {
            localeMappings {
              path
              locale
            }
          }
        }
      `}
      render={data => (
        <Layout data={data} cmsTheme={cmsTheme} lang={lang}>
          {children}
        </Layout>
      )}
    />
  );
};

withStaticQueryRedirect.propTypes = {
  children: PropTypes.node.isRequired,
  cmsTheme: PropTypes.shape(),
  lang: PropTypes.string,
};

withStaticQueryRedirect.defaultProps = {
  cmsTheme: null,
  lang: 'en-ca',
};

Layout.propTypes = {
  data: PropTypes.shape().isRequired,
  children: PropTypes.node.isRequired,
  cmsTheme: PropTypes.shape(),
  lang: PropTypes.string,
};

Layout.defaultProps = {
  cmsTheme: null,
  lang: 'en-ca',
};

export default withStaticQueryRedirect;
